'use client';
import React, { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';

import { SessionContext } from '@benzinga/session-context';
import { useSophiTracking } from '@benzinga/user-context';
import { UserManager } from '@benzinga/user-manager';
import { JsonLd, Meta, MetaProps } from '@benzinga/seo';
import { ClickTracker, GeoTracker, PageTracker } from '@benzinga/analytics';

import { NoFirstRender } from '@benzinga/hooks';
import { sophiManager } from '@benzinga/ads-utils';
import { formatNavigationSchema } from '@benzinga/frontend-utils';
import { AuthPortal } from '@benzinga/auth-ui';
import { menus } from '@benzinga/navigation-ui';
import { PageProps } from '../../../src/components/Page';
import { GlobalTracking } from '../../../src/components/Tracking/GlobalTracking';

interface RootTrackerProps {
  pageProps?: PageProps;
}

const RootTracker: React.FC<RootTrackerProps> = ({ pageProps }) => {
  const [meta, setMeta] = useState<MetaProps>(pageProps?.metaProps as MetaProps);
  const [isSophiReady, setIsSophiReady] = useState(false);
  const session = React.useContext(SessionContext);
  const userManager = session.getManager(UserManager);
  const router = useRouter();
  const pathname = usePathname();
  const canonical = meta?.canonical ?? `https://www.benzinga.com${pathname}`;
  const shouldPageTrack = pageProps?.statusCode !== 404 && !pageProps?.disablePageTracking && pageProps?.error !== 404;

  const handleOnRegister = React.useCallback(() => {
    router.push('/welcome');
  }, [router]);

  useEffect(() => {
    const listener = (e: Event) => {
      const customEvent = e as CustomEvent<any>;
      if (customEvent?.detail?.trackMeta !== false) {
        setMeta(customEvent?.detail);
      }
    };
    window.addEventListener('page-transition', listener);
    return () => {
      window.removeEventListener('page-transition', listener);
    };
  }, []);

  const { trackPageView } = useSophiTracking({
    article: pageProps?.article,
    meta: pageProps?.metaProps,
    post: pageProps?.post || pageProps?.page,
    term: pageProps?.term,
    pageTargeting: pageProps?.pageTargeting,
  });

  useEffect(() => {
    (async () => {
      sophiManager.init(async () => {
        setIsSophiReady(true);
        const user = await global.analytics?.user();
        const anonymousId = user?.anonymousId();
        const userId = user?.id();
        if (userId || anonymousId) {
          const segmentUserTraits = await userManager.getSegmentUserTraits({
            userId: userId ?? undefined,
            anonymousId: anonymousId ?? undefined,
          });
          if (segmentUserTraits.ok) {
            await sophiManager.setUserProperties(segmentUserTraits.ok);
          }
        }
        trackPageView();
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <GlobalTracking pageProps={pageProps ?? {}} session={session.getSession()} />
      <NoFirstRender>
        <React.Suspense fallback={null}>
          <ClickTracker article={pageProps?.article} post={pageProps?.post || pageProps?.page} />
          <GeoTracker />
          <AuthPortal authMode="register" onRegister={handleOnRegister} />
        </React.Suspense>
      </NoFirstRender>
      {shouldPageTrack && isSophiReady && <PageTracker enableCoralogixRum={true} routerType="app" />}
      {meta && <Meta {...meta} canonical={canonical} disableGlobalStructuredData disablePageAndCompanyStructuredData />}
      <JsonLd data={formatNavigationSchema(menus.primary)} id="navigation-header-schema" />
    </>
  );
};

export default RootTracker;
